import { useCallback, useEffect, useRef, useState } from 'react';

// Default centre used when geolocation is unavailable or denied
// (Allure's launch city — Corfu, Greece).
const DEFAULT_CENTER = [39.6243, 19.9217];

// Prefer the Capacitor Geolocation plugin when the app is running inside the
// native Android WebView, because it exposes a proper `requestPermissions()`
// API that can re-prompt after a previous denial. In the regular web build
// (browsers, PWA) we fall back to the standard `navigator.geolocation` API.
const getCapacitorGeolocation = () => {
  if (typeof window === 'undefined') return null;
  const cap = window.Capacitor;
  if (!cap?.isNativePlatform || !cap.isNativePlatform()) return null;
  return cap?.Plugins?.Geolocation || null;
};

export function useUserLocation() {
  const [userPosition, setUserPosition] = useState(null);
  const [locationStatus, setLocationStatus] = useState('requesting');
  const watchIdRef = useRef(null);
  const capWatchIdRef = useRef(null);

  const startWatch = useCallback(async () => {
    const capGeo = getCapacitorGeolocation();
    if (capGeo) {
      try {
        // Ask the native runtime first so a previously-denied user gets a
        // fresh system prompt (unless they ticked "don't ask again").
        const perm = await capGeo.requestPermissions();
        if (perm?.location === 'denied' && perm?.coarseLocation === 'denied') {
          setUserPosition(DEFAULT_CENTER);
          setLocationStatus('fallback');
          return;
        }
        const pos = await capGeo.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
        setUserPosition([pos.coords.latitude, pos.coords.longitude]);
        setLocationStatus('live');
        // Also start a watch for updates while the user moves.
        if (capWatchIdRef.current) {
          try { await capGeo.clearWatch({ id: capWatchIdRef.current }); } catch { /* ignore */ }
        }
        capWatchIdRef.current = await capGeo.watchPosition(
          { enableHighAccuracy: true },
          (position, err) => {
            if (err || !position) return;
            setUserPosition([position.coords.latitude, position.coords.longitude]);
            setLocationStatus('live');
          },
        );
        return;
      } catch {
        setUserPosition(DEFAULT_CENTER);
        setLocationStatus('fallback');
        return;
      }
    }

    if (!navigator.geolocation) {
      setLocationStatus('fallback');
      setUserPosition(DEFAULT_CENTER);
      return;
    }
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setUserPosition([pos.coords.latitude, pos.coords.longitude]);
        setLocationStatus('live');
      },
      () => {
        setUserPosition(DEFAULT_CENTER);
        setLocationStatus('fallback');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 },
    );
  }, []);

  useEffect(() => {
    startWatch();
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      const capGeo = getCapacitorGeolocation();
      if (capGeo && capWatchIdRef.current) {
        capGeo.clearWatch({ id: capWatchIdRef.current }).catch(() => {});
      }
    };
  }, [startWatch]);

  // Manual re-request — used by the map "recenter" button so a user who
  // denied location the first time can grant it later without leaving the
  // page. On web it retriggers the browser prompt; on Capacitor it invokes
  // `requestPermissions()` again (which is the correct way to re-prompt an
  // Android runtime permission that wasn't marked "don't ask again").
  const requestLocation = useCallback(() => {
    setLocationStatus('requesting');
    return startWatch();
  }, [startWatch]);

  return { userPosition, locationStatus, defaultCenter: DEFAULT_CENTER, requestLocation };
}
